import { MobXProviderContext } from 'mobx-react';
import { ContactModelV2 } from 'Models/ContactModelV2';
import { useInfiniteContacts } from 'Modules/contacts/requests';
import { DEFAULT_CONTACTS_LIMIT } from 'Modules/conversation/createConversation/components/createConversationForm/constants';
import { useContactTypeResolver, mapContactToOption, createContactFilterOption } from 'Modules/conversation/createConversation/components/createConversationForm/resolver';
import { createConversationSchema } from 'Modules/conversation/createConversation/components/createConversationForm/schema';
import { useConversationErrors } from 'Modules/conversation/createConversation/hooks/useConversationErrors';
import { useConversationFormEffects } from 'Modules/conversation/createConversation/hooks/useConversationFormEffects';
import { useConversationManager } from 'Modules/conversation/createConversation/hooks/useConversationManager';
import type { ContactOptionType } from 'Modules/conversation/createConversation/types';
import { determineConversationType } from 'Modules/conversation/createConversation/utils';
import { useConversationStore } from 'Modules/conversation/store/index.store';
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useNavigate } from 'react-router';
import type { RootStoreProps } from 'Stores/RootStore.types';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { z } from 'zod';
import { Autocomplete } from '~/lib/ui/autocomplete';
import { Button } from '~/lib/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/lib/ui/form';
import { Input } from '~/lib/ui/input';
import { cn } from '~/lib/utils';

/**
 * Form component for creating new conversations.
 * Handles both automatic conversation creation/preview and explicit form submission.
 */
export function CreateConversationForm() {
  const navigate = useNavigate();
  const previousContactsRef = useRef<string>('');

  // Add MobX context access for conversation store and person store
  const { conversation, person } =
    React.useContext<RootStoreProps>(MobXProviderContext);

  // State management for search functionality
  const [searchQuery, setSearchQuery] = useState('');

  // Conversation store state for selected contacts
  const {
    selectedContacts,
    setSelectedContacts,
    setPreviewConversationId,
    setIsAdHocGroupCreation,
  } = useConversationStore();

  const { handleSubmissionError, handleGeneralError } = useConversationErrors();

  // Conversation creation utilities
  const {
    convertContactsToParticipants,
    createOneToOne,
    createAdHocGroup,
    createNamedGroup,
    checkExistingAdHocGroup,
  } = useConversationManager();

  // Convert selected contacts to participants format
  const participants = convertContactsToParticipants(selectedContacts);

  // Infinite contacts query for search functionality
  const {
    data: contactsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteContacts(searchQuery, DEFAULT_CONTACTS_LIMIT);

  /**
   * Process contact data from the infinite query into autocomplete options.
   * Flattens paginated results and maps contacts to option format.
   * Filters out the current user from the contacts list.
   */
  const contactOptionsFromQuery = useMemo(() => {
    const currentContacts =
      contactsData?.pages?.flatMap(
        (page) => page.contacts as ContactModelV2[]
      ) ?? [];

    // Filter out the current user from the contacts list
    const filteredContacts = currentContacts.filter(
      (contact) => contact.sourceId !== person.loggedInPersonId.toString()
    );

    // Map contacts to options and sort alphabetically by name (case-insensitive)
    return filteredContacts
      .map(mapContactToOption)
      .sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }));
  }, [contactsData, person.loggedInPersonId]);

  /**
   * Resolve contact types and process options for the autocomplete component.
   * Enhanced to include phone number detection and real-time option creation.
   */
  const { processedContactOptions, getTooltipInfo, isFirstContactInternal } =
    useContactTypeResolver({
      selectedContacts,
      baseContacts: contactOptionsFromQuery,
      searchQuery,
    });

  // Form effects and validation setup
  const form = useConversationFormEffects({
    selectedContacts,
    isError,
  });

  const {
    formState: { isValid },
  } = form;

  // Generate a stable key for the selected contacts
  const getContactsKey = useCallback(
    (contacts: ContactOptionType[]) =>
      contacts
        .map((contact) => contact.value)
        .sort()
        .join(','),
    []
  );

  /**
   * Automatic conversation creation/preview effect when contacts change
   * Enhanced to handle unsaved contacts properly
   */
  useEffect(() => {
    const currentKey = getContactsKey(selectedContacts);
    const previousKey = previousContactsRef.current;

    if (previousKey === currentKey) {
      return;
    };

    previousContactsRef.current = currentKey;

    // Clear preview when no contacts
    if (selectedContacts.length === 0) {
      setPreviewConversationId(null);
      setIsAdHocGroupCreation(false);
      return;
    }

    const conversationType = determineConversationType(selectedContacts);

    // Handle automatic creation/preview based on conversation type
    if (conversationType === 'one-to-one') {
      setIsAdHocGroupCreation(false);

      // Create one-to-one conversations automatically for all contacts (internal, external, and unsaved)
      createOneToOne({ participants }).catch((error) => {
        console.error('[conversation] createOneToOne:error', error);
      });
    } else if (conversationType === 'adhoc-group') {
      // Set channel info details before checking existing group
      const hasExternalContacts = selectedContacts.some(
        (contact) => contact.type === 'external-contact' || contact.type === 'unsaved-contact'
      );
      const channelType = hasExternalContacts ? 'phone' : 'person';

      // Set channel info details similar to the old TopBarDropdown flow
      conversation.setChannelInfoDetails('0', 'new', channelType);

      // Check if all contacts are external/unsaved (not internal)
      const allExternalOrUnsaved = selectedContacts.every(
        (contact) => contact.type === 'external-contact' || contact.type === 'unsaved-contact'
      );

      if (allExternalOrUnsaved) {
        // For external/unsaved contacts, create ad-hoc group automatically
        createAdHocGroup({ participants }).catch((error) => {
          console.error('[conversation] createAdHocGroup:error', error);
        });
      } else {
        // For internal contacts, check if ad-hoc group exists (preview only)
        checkExistingAdHocGroup({ participants }).catch((error) => {
          console.error('[conversation] checkExistingAdHocGroup:error', error);
        });
      }
    }
  }, [
    selectedContacts,
    getContactsKey,
    setPreviewConversationId,
    setIsAdHocGroupCreation,
    convertContactsToParticipants,
    createOneToOne,
    createAdHocGroup,
    checkExistingAdHocGroup,
    participants,
    conversation,
  ]);

  /**
   * Load more contacts when user scrolls to bottom of autocomplete menu.
   * Prevents duplicate requests while loading.
   */
  const loadMoreContacts = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  /**
   * Handle search input changes to update the query for contact search.
   * Enhanced to provide immediate feedback for phone numbers.
   * @param inputValue - The current input value from the autocomplete
   */
  const handleInputChange = useCallback((inputValue: string) => {
    setSearchQuery(inputValue);
  }, []);

  /**
   * Handle form submission for conversation creation.
   * Enhanced to handle unsaved contacts properly.
   */
  const onSubmit = async (data: z.infer<typeof createConversationSchema>) => {
    // Early validation
    if (!selectedContacts.length) {
      console.warn('No contacts selected for conversation creation');
      return;
    }

    // Determine conversation type based on contacts and group name
    const conversationType = determineConversationType(
      selectedContacts,
      data.groupName
    );

    if (!conversationType) {
      console.warn('Unable to determine conversation type');
      return;
    }

    try {
      const result = await handleConversationCreation(conversationType, data);

      if (result) {
        // Navigate to the created conversation
        void navigate(`/chat/conversations/${result.id}/menu`);
      }
    } catch (error) {
      handleSubmissionError(error, conversationType, data, selectedContacts);
    }
  };

  /**
   * Handles the actual conversation creation based on type
   * Enhanced to handle unsaved contacts
   */
  const handleConversationCreation = async (
    conversationType: 'one-to-one' | 'named-group' | 'adhoc-group',
    data: z.infer<typeof createConversationSchema>
  ) => {
    switch (conversationType) {
      case 'named-group':
        return await createNamedGroup({
          participants,
          topic: data.groupName,
          description: data.groupDescription,
          grouping: 'Channel',
          onError: (errorInfo) => {
            if (errorInfo.type === 'GROUP_NAME_EXISTS') {
              form.setError('groupName', {
                message: 'Group name already exists',
              });
            } else {
              handleGeneralError(errorInfo.error, 'named group creation');
            }
          },
        });

      case 'one-to-one':
        return await createOneToOne({ participants });

      case 'adhoc-group': {
        const hasInternalContacts = selectedContacts.some(
          (contact) => contact.type === 'internal-contact'
        );

        // Only create ad-hoc group for internal contacts without group name
        if (hasInternalContacts && !data.groupName?.trim()) {
          return await createAdHocGroup({ participants });
        }

        // For external/unsaved contacts, conversation is already created automatically
        return null;
      }

      default:
        throw new Error(
          `Unsupported conversation type: ${conversationType as string}`
        );
    }
  };

  return (
    <div className="w-full">
      <h2 className="text-base font-bold text-foreground">
        Start conversation
      </h2>
      <Form {...form}>
        <form className="space-y-8 mt-6">
          {/* Contact Selection Autocomplete */}
          <FormField
            control={form.control}
            name="selectedContacts"
            render={({ field }) => (
              <div className={cn(!isFirstContactInternal && 'mb-6')}>
                <FormItem>
                  <FormControl>
                    <Autocomplete
                      label="To:"
                      placeholder="search name or number"
                      value={field.value || selectedContacts}
                      options={processedContactOptions}
                      filterOption={createContactFilterOption}
                      onChange={(selectedOptions) => {
                        setSelectedContacts(
                          selectedOptions as ContactOptionType[]
                        );
                        field.onChange(selectedOptions);
                      }}
                      onInputChange={handleInputChange}
                      onMenuScrollToBottom={loadMoreContacts}
                      isLoading={isLoading || isFetchingNextPage}
                      getTooltipInfo={getTooltipInfo}
                      autoFocus={true}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              </div>
            )}
          />

          {selectedContacts.length > 0 && isFirstContactInternal && (
            <>
              <div className="flex flex-row w-full gap-4">
                <div className="grid grid-cols-2 gap-4 w-full">
                  {/* Group Name Input */}
                  <FormField
                    control={form.control}
                    name="groupName"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Group Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Add a group name" {...field} />
                        </FormControl>
                        <div className="min-h-[24px] flex items-start">
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />

                  {/* Group Description Input */}
                  <FormField
                    control={form.control}
                    name="groupDescription"
                    render={({ field }) => (
                      <FormItem className="flex flex-col">
                        <FormLabel>Group Description</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Add a group description"
                            {...field}
                          />
                        </FormControl>
                        <div className="min-h-[24px] flex items-start">
                          <FormMessage />
                        </div>
                      </FormItem>
                    )}
                  />
                </div>
                {/* Create Button */}
                <div className="-mt-3 flex items-center justify-center">
                  <Button
                    data-testid="create-conversation-button"
                    variant="outline"
                    type="button"
                    size="lg"
                    disabled={!isValid || selectedContacts.length === 0}
                    onClick={() => {
                      // Track create button click
                      pushToGTMDataLayer('new_conversation_create', {
                        groupName: form.getValues('groupName'),
                        participantCount: selectedContacts.length,
                      });

                      void form.handleSubmit(onSubmit)();
                    }}
                  >
                    Create
                  </Button>
                </div>
              </div>
            </>
          )}
        </form>
      </Form>
    </div>
  );
}
